import { useCallback, useEffect, useRef } from "react";
import type { ChatSubagentRun, ChatWorkflowRun } from "@/lib/chat";
import { fetchChatRuns } from "@/lib/chat-runs";
import { useChatStore } from "@/stores/useChatStore";
import { useToastStore } from "@/stores/useToastStore";

const POLL_INTERVAL_MS = 2_000;

/**
 * 対象チャットのサブエージェント実行・ワークフロー実行の一覧を取得し、ストアへ反映する。
 * runTools が空の場合は状態をクリアし、実行中または run id 未解決のものがあれば
 * 定期ポーリングで追従する。
 */
export function useChatRunsSync(
	chatId: string | null,
	runTools: readonly { toolUseId: string; name: string; status: string }[],
	shouldPoll: boolean,
	setSubagentRuns: (runs: ChatSubagentRun[]) => void,
	setWorkflowRuns: (runs: ChatWorkflowRun[]) => void,
): void {
	// トーストを出した対象チャット。真偽値にすると ChatView は一度しかマウントされない
	// (key={chatId} を持たない) ためチャットをまたいで持ち越し、切り替えた先の障害が無音になる。
	const errorShownForRef = useRef<string | null>(null);
	// 応答の適用順を発行順に揃えるための連番。取得は中断できず (一覧 API は AbortSignal を取らない)、
	// 2 秒間隔より遅い応答は追い越されうる。古い応答をそのまま書くと、終端した workflow が
	// 実行中へ巻き戻り、その巻き戻しは runToolSignature を変えないため再取得も走らない。
	const issuedSeqRef = useRef(0);
	const appliedSeqRef = useRef(0);
	// 新しい起動の出現、および tool_result 到着 (= ステータス変化) の両方で再取得させるための
	// 依存キー。tool_use だけが先に永続化される (run 行の作成は後追い) ため、ステータスも
	// キーに含めないと run 行がまだ読めないタイミングで取得を止めてしまう。
	const runToolSignature = runTools.map((t) => `${t.toolUseId}:${t.status}`).join(",");
	const hasRunTools = runToolSignature.length > 0;
	// workflow を起動していないチャットで一覧を引いても必ず空が返る。取得はツールがある時だけに絞る。
	const hasWorkflowTools = runTools.some((tool) => tool.name === "run_workflow");

	const syncRuns = useCallback(
		async (targetChatId: string) => {
			const seq = ++issuedSeqRef.current;
			const { subagentRuns, workflowRuns, failure } = await fetchChatRuns(
				targetChatId,
				hasWorkflowTools,
			);
			if (useChatStore.getState().chatId !== targetChatId) return;
			// より新しい応答を既に反映済みなら、この結果は捨てる。失敗の扱いも新しいほうが正。
			if (seq < appliedSeqRef.current) return;
			appliedSeqRef.current = seq;
			// 取れたほうだけ反映する。片方が落ちている間ももう片方は最新に保つ。
			if (subagentRuns !== null) setSubagentRuns(subagentRuns);
			if (workflowRuns !== null) setWorkflowRuns(workflowRuns);
			if (failure === null) {
				if (errorShownForRef.current === targetChatId) errorShownForRef.current = null;
				return;
			}
			console.error("実行の状態取得に失敗しました", failure);
			if (errorShownForRef.current !== targetChatId) {
				errorShownForRef.current = targetChatId;
				useToastStore
					.getState()
					.show({ type: "error", message: "実行の状態を取得できませんでした。" });
			}
		},
		[hasWorkflowTools, setSubagentRuns, setWorkflowRuns],
	);

	useEffect(() => {
		if (chatId === null || !hasRunTools) {
			setSubagentRuns([]);
			setWorkflowRuns([]);
			return;
		}
		void syncRuns(chatId);
		// runToolSignature は runTools の変化を検出するための依存値。
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chatId, runToolSignature, hasRunTools, syncRuns]);

	useEffect(() => {
		if (chatId === null || !shouldPoll) return;
		const timer = setInterval(() => {
			void syncRuns(chatId);
		}, POLL_INTERVAL_MS);
		return () => clearInterval(timer);
	}, [chatId, shouldPoll, syncRuns]);
}
